import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CompanyMonogram } from "@/components/CompanyMonogram";

export const LogosTab = () => {
  const [opps, setOpps] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/opportunities").then((r) => setOpps(r.data)).catch(() => {}); }, []);

  const patch = (slug, logo_path) => setOpps((prev) => prev.map((o) => (o.slug === slug ? { ...o, logo_path } : o)));

  const upload = async (o, file) => {
    if (!file) return;
    setBusy(o.slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/opportunities/${o.slug}/logo`, fd);
      patch(o.slug, res.data.logo_path);
      toast.success(`${o.name}: logo updated`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const remove = async (o) => {
    setBusy(o.slug);
    try { await api.delete(`/admin/opportunities/${o.slug}/logo`); patch(o.slug, null); toast.success(`${o.name}: back to monogram`); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const shown = opps.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()));
  const withLogo = opps.filter((o) => o.logo_path).length;

  return (
    <div data-testid="admin-logos-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Company Logos</h2>
          <p className="mt-1 text-sm text-slate-500">Upload each company's official logo (PNG/JPG/WEBP, ≤2 MB, square works best). Cards fall back to a monogram until a logo is set.</p>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="admin-logos-count" className="border border-[#6D28D9]/40 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#6D28D9]">{withLogo} / {opps.length} logos</span>
          <input data-testid="admin-logos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company…" className="w-56 border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none focus:border-[#6D28D9]/60" />
        </div>
      </div>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((o) => (
          <div key={o.slug} data-testid={`admin-logo-row-${o.slug}`} className="flex items-center gap-4 border border-slate-200 bg-[#F6F4FD]/70 p-4">
            <CompanyMonogram name={o.name} logo={o.logo_path} size="md" testid={`admin-logo-preview-${o.slug}`} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-800">{o.name}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{o.logo_path ? "Official logo" : "Monogram"}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <label data-testid={`admin-logo-upload-${o.slug}`} className="flex cursor-pointer items-center gap-1.5 bg-[#6D28D9] px-3 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#5B21B6]">
                {busy === o.slug ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} {o.logo_path ? "Replace" : "Upload"}
                <input data-testid={`admin-logo-input-${o.slug}`} type="file" accept="image/*" className="hidden" disabled={busy === o.slug} onChange={(e) => upload(o, e.target.files?.[0])} />
              </label>
              {o.logo_path && (
                <button data-testid={`admin-logo-remove-${o.slug}`} onClick={() => remove(o)} disabled={busy === o.slug} className="border border-slate-200 p-2 text-slate-400 transition-colors hover:border-rose-300 hover:text-rose-600 disabled:opacity-40" aria-label="Remove logo"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
